export const API_ENDPOINT =
  process.env.REACT_APP_API_URL ??
  (process.env.NODE_ENV === "production"
    ? "https://api.eventshouse.net/api"
    : "http://127.0.0.1:5000/api");

const TOKEN_KEY = "eventshouse_auth_token";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function authHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatApiErrorMessage(body: Record<string, unknown>): string {
  const error = body.error as
    | {
        message?: string;
        details?: Array<{ field?: string; message?: string }>;
      }
    | undefined;
  const details = error?.details;
  if (Array.isArray(details) && details.length > 0) {
    const fieldMessages = details
      .map((detail) => detail.message?.trim())
      .filter((message): message is string => Boolean(message));
    if (fieldMessages.length > 0) {
      return fieldMessages.join(" ");
    }
  }
  return error?.message ?? (body.message as string | undefined) ?? "Request failed";
}

export async function parseApiResponse<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok || body.success === false) {
    throw new Error(formatApiErrorMessage(body));
  }
  return body.data as T;
}

/**
 * Wrapped fetch that automatically attaches the Authorization header and
 * dispatches a global `auth:unauthorized` event when the server returns 401.
 *
 * The GlobalContext listens for the event and clears currentUser + token so a
 * silently expired or revoked session results in the user being logged out.
 */
export async function fetchWithAuth(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (init.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, { ...init, headers });

  if (response.status === 401) {
    setAuthToken(null);
    window.dispatchEvent(new Event("auth:unauthorized"));
  }

  return response;
}
